import { useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import useShortcuts from '../../hooks/useShortcuts'
import { selectActiveChartId } from '../../selectors/chart'
import { AppContext } from '../../App'
import { IndicatorsDrawingShortcutsKeyName } from './ShortcutsKeymap'
import { ShortcutsCategories, ShortcutType } from './Shortcuts.types'
import { DrawingDisplayType } from '../chartV2/drawingTools/drawing.interface'

type IndicatorsDrawingsShortcutsRegisterProps = {
    viewKey: string
}

/**
 * Registers keyboard shortcuts for indicator and drawing tools on a single chart instance.
 * Shortcuts are only active when this chart's viewKey matches the active chart id.
 */
const IndicatorsDrawingsShortcutsRegister = ({ viewKey }: IndicatorsDrawingsShortcutsRegisterProps) => {
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const isActiveChart = viewKey === activeChartId

    /**
     * Activates a drawing tool on this chart.
     * Mirrors the logic in chartManager.selectDrawingTool.
     */
    const activateDrawing = useCallback(
        (drawingType: string, toolOptions: Record<string, any> = {}) => {
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            chart.drawingManager.addDrawing(
                drawingType as any,
                DrawingDisplayType.DRAWING,
                toolOptions,
                {}
            )
        },
        [appContext, viewKey]
    )

    const shortcuts: ShortcutType = useMemo(() => {
        const map: ShortcutType = {}

        // ─── Drawing tool activations ────────────────────────────────────────────
        map[IndicatorsDrawingShortcutsKeyName.trendline] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Trendline', { source: 'keyboard' })
            activateDrawing('trendLine', { drawingType: 'trendLine' })
        }

        map[IndicatorsDrawingShortcutsKeyName.horizontalLine] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Horizontal Line', { source: 'keyboard' })
            activateDrawing('horizontalLine', { drawingType: 'horizontalLine' })
        }

        map[IndicatorsDrawingShortcutsKeyName.horizontalRay] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Horizontal Ray', { source: 'keyboard' })
            activateDrawing('horizontalLine', { drawingType: 'horizontalRay' })
        }

        map[IndicatorsDrawingShortcutsKeyName.verticalLine] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Vertical Line', { source: 'keyboard' })
            activateDrawing('verticalLine')
        }

        map[IndicatorsDrawingShortcutsKeyName.crossline] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Cross Line', { source: 'keyboard' })
            activateDrawing('crossLine')
        }

        map[IndicatorsDrawingShortcutsKeyName.fibRetracement] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Fibonacci Retracement', { source: 'keyboard' })
            activateDrawing('fibonacci')
        }

        map[IndicatorsDrawingShortcutsKeyName.rectangle] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Rectangle', { source: 'keyboard' })
            activateDrawing('rectangle')
        }

        map[IndicatorsDrawingShortcutsKeyName.circle] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Circle', { source: 'keyboard' })
            activateDrawing('circle')
        }

        map[IndicatorsDrawingShortcutsKeyName.ellipse] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Drawing Tool: Ellipse', { source: 'keyboard' })
            activateDrawing('ellipse')
        }

        // ─── Drawing management shortcuts ────────────────────────────────────────
        map[IndicatorsDrawingShortcutsKeyName.hideAllDrawings] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Hide All Drawings', { source: 'keyboard' })
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            // Toggle visibility of all drawings
            const allDrawings = Object.values(chart.drawingManager.drawings)
            allDrawings.forEach((dr) => {
                dr.visible = false
                dr.updateDrawing?.()
            })
        }

        map[IndicatorsDrawingShortcutsKeyName.removeObject] = (event: KeyboardEvent) => {
            event.preventDefault()
            AnalyticsManager.logEvent('Remove Selected Drawing', { source: 'keyboard' })
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            // Remove the currently active/focused drawing
            const activeDrawing = Object.values(chart.drawingManager.drawings).find((dr) => dr._active)
            if (activeDrawing) {
                activeDrawing.onDestroy(activeDrawing.id, true)
            }
        }

        map[IndicatorsDrawingShortcutsKeyName.copySelectedObject] = (_event: KeyboardEvent) => {
            // Let the browser's default copy handle this; the drawing manager may listen for it
            AnalyticsManager.logEvent('Copy Selected Drawing', { source: 'keyboard' })
        }

        map[IndicatorsDrawingShortcutsKeyName.pasteObject] = (_event: KeyboardEvent) => {
            // Let the browser's default paste handle this; the drawing manager may listen for it
            AnalyticsManager.logEvent('Paste Drawing', { source: 'keyboard' })
        }

        map[IndicatorsDrawingShortcutsKeyName.moveDrawingLeft] = (event: KeyboardEvent) => {
            event.preventDefault()
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            const activeDrawing = Object.values(chart.drawingManager.drawings).find((dr) => dr._active)
            ;(activeDrawing as any)?.moveLeft?.()
        }

        map[IndicatorsDrawingShortcutsKeyName.moveDrawingRight] = (event: KeyboardEvent) => {
            event.preventDefault()
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            const activeDrawing = Object.values(chart.drawingManager.drawings).find((dr) => dr._active)
            ;(activeDrawing as any)?.moveRight?.()
        }

        map[IndicatorsDrawingShortcutsKeyName.moveDrawingUp] = (event: KeyboardEvent) => {
            event.preventDefault()
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            const activeDrawing = Object.values(chart.drawingManager.drawings).find((dr) => dr._active)
            ;(activeDrawing as any)?.moveUp?.()
        }

        map[IndicatorsDrawingShortcutsKeyName.moveDrawingDown] = (event: KeyboardEvent) => {
            event.preventDefault()
            const chart = appContext.getChart(viewKey)
            if (!chart?.drawingManager) return
            const activeDrawing = Object.values(chart.drawingManager.drawings).find((dr) => dr._active)
            ;(activeDrawing as any)?.moveDown?.()
        }

        return map
    }, [activateDrawing, appContext, viewKey])

    useShortcuts(ShortcutsCategories.INDICATORS_AND_DRAWINGS, shortcuts, isActiveChart)

    return null
}

export default IndicatorsDrawingsShortcutsRegister
